import React from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Monitor, 
  Smartphone, 
  TrendingUp, 
  Users, 
  Eye, 
  Clock, 
  Brain, 
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Calendar,
  BarChart3,
  Target,
  Image,
  Video,
  SquareStack
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useWatch } from '@/contexts/WatchContext'
import { useWatchedDeviceService } from '@/hooks/useWatchedDeviceService'
import { useWatchedScreenService } from '@/hooks/useWatchedScreenService'
import AdminBadge from '@/components/UI/AdminBadge'
import { getDeviceLimit, getScreenLimit } from '@/utils/subscriptionUtils'
import { hasAdminPrivileges } from '@/utils/adminUtils'
import SubscriptionService from '@/utils/services/SubscriptionService'
import userService from '@/utils/services/UserService'

const Dashboard = () => {
  const { currentUser, currentUserData, loading: authLoading } = useAuth()
  const { getEffectiveUser } = useWatch()
  const navigate = useNavigate()
  const { devices, loading: devicesLoading, stats: deviceStats } = useWatchedDeviceService()
  const { screens, loading: screensLoading } = useWatchedScreenService()

  // State for subscription data
  const [subscriptions, setSubscriptions] = React.useState([])
  const [userSubscriptions, setUserSubscriptions] = React.useState([])

  // Combined loading state - include auth loading and check for user data
  const loading = authLoading || devicesLoading || screensLoading || !currentUserData

  // Load subscription data and calculate device/screen limits
  React.useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        // Load subscriptions and user subscriptions
        const [subscriptionsData, userSubs] = await Promise.all([
          SubscriptionService.loadSubscriptions(true),
          userService.getUserSubscriptions(currentUser.uid, true)
        ])
        
        setSubscriptions(subscriptionsData)
        setUserSubscriptions(userSubs)
      } catch (error) {
        console.error('Error loading subscription data:', error)
      }
    }

    if (currentUser && currentUserData) {
      loadSubscriptionData()
    }
  }, [currentUser, currentUserData])

  // Debug logging
  console.log('🔍 Dashboard - currentUserData:', currentUserData?.displayNameOrEmail, 'devices:', devices.length, 'deviceStats:', deviceStats, 'screens:', screens.length, 'loading:', loading)
  console.log('🔍 Dashboard - currentUserData full:', currentUserData)

  // Use computed properties from DeviceService with fallback to devices array
  const onlineDevices = deviceStats?.online || devices.filter(d => d.isOnline).length || 0
  const totalDevices = deviceStats?.total || devices.length || 0
  const assignedDevices = deviceStats?.assigned || devices.filter(d => d.screenId).length || 0

  // Calculate total schedules across all devices
  const totalSchedules = devices.reduce((total, device) => {
    return total + (device.scheduleCount || 0)
  }, 0)

  // Calculate additional metrics
  const offlineDevices = totalDevices - onlineDevices
  const onlinePercentage = totalDevices > 0 ? Math.round((onlineDevices / totalDevices) * 100) : 0
  const avgSchedulesPerDevice = totalDevices > 0 ? Math.round((totalSchedules / totalDevices) * 10) / 10 : 0

  // Calculate screen type breakdown
  const screenTypeBreakdown = screens.reduce((acc, screen) => {
    if (screen.videoUrl) {
      acc.video++
    } else if (screen.slides && screen.slidesData && screen.slidesData.length > 0) {
      acc.slideshow++
    } else {
      acc.singleImage++
    }
    return acc
  }, { singleImage: 0, slideshow: 0, video: 0 })

  // Debug logging for screen type breakdown
  console.log('🔍 Screen type breakdown:', screenTypeBreakdown)

  // Get effective user data (current user or watched user)
  const effectiveUserData = getEffectiveUser()

  // Get device limit for effective user's subscription
  const deviceLimit = getDeviceLimit(effectiveUserData, userSubscriptions, subscriptions)
  const deviceLimitText = deviceLimit === Infinity ? 'Unlimited' : deviceLimit.toString()
  console.log('🔍 Dashboard - deviceLimit:', deviceLimit, 'deviceLimitText:', deviceLimitText)

  // Get screen limit for effective user's subscription
  const screenLimit = getScreenLimit(effectiveUserData, userSubscriptions, subscriptions)
  const screenLimitText = screenLimit === Infinity ? 'Unlimited' : screenLimit.toString()
  console.log('🔍 Dashboard - screenLimit:', screenLimit, 'screenLimitText:', screenLimitText)

  // Create screen type breakdown text
  const getScreenTypeBreakdown = () => {
    const parts = []
    if (screenTypeBreakdown.singleImage > 0) {
      parts.push(`${screenTypeBreakdown.singleImage} Single Image${screenTypeBreakdown.singleImage > 1 ? 's' : ''}`)
    }
    if (screenTypeBreakdown.slideshow > 0) {
      parts.push(`${screenTypeBreakdown.slideshow} Slideshow${screenTypeBreakdown.slideshow > 1 ? 's' : ''}`)
    }
    if (screenTypeBreakdown.video > 0) {
      parts.push(`${screenTypeBreakdown.video} Video Clip${screenTypeBreakdown.video > 1 ? 's' : ''}`)
    }
    return parts.length > 0 ? parts.join(', ') : 'No screens'
  }

  // Calculate stats using DeviceService data
  const stats = [
    {
      title: 'Active Screens',
      value: screens.length.toString(),
      change: `${screens.length}/${screenLimitText} screens`,
      changeType: screenLimit === Infinity || screens.length < screenLimit ? 'positive' : 'negative',
      icon: Monitor,
      color: 'bg-blue-500'
    },
    {
      title: 'Connected Devices',
      value: totalDevices.toString(),
      change: `${onlineDevices}/${totalDevices} online (${deviceLimitText} limit)`,
      changeType: 'positive',
      icon: Smartphone,
      color: 'bg-green-500'
    },
    {
      title: 'Online Devices',
      value: onlineDevices.toString(),
      change: `${onlinePercentage}% online`,
      changeType: onlinePercentage > 50 ? 'positive' : 'negative',
      icon: Activity,
      color: 'bg-purple-500'
    },
    {
      title: 'Schedules',
      value: totalSchedules.toString(),
      change: `${avgSchedulesPerDevice} avg per device`,
      changeType: 'positive',
      icon: Calendar,
      color: 'bg-orange-500'
    }
  ]

  // Generate recent activity from real data using DeviceService computed properties
  const recentActivity = [
    ...devices.slice(0, 3).map((device, index) => ({
      id: `device-${device.key}`,
      type: 'device_connected',
      title: `Device "${device.name}" ${device.isOnline ? 'is online' : 'went offline'}`,
      time: device.lastAccessFormatted || 'Never',
      user: 'System'
    })),
    ...screens.slice(0, 2).map((screen, index) => ({
      id: `screen-${screen.key}`,
      type: 'screen_created',
      title: `Screen "${screen.name}" is active`,
      time: screen.created ? new Date(screen.created).toLocaleString() : 'Unknown',
      user: 'System'
    }))
  ].slice(0, 5) // Limit to 5 items

  // Generate dynamic AI insights based on user data
  const generateAIInsights = () => {
    const insights = []
    
    // Screen-based insights
    if (screens.length > 0) {
      const screenTypes = {
        singleImage: screens.filter(s => !s.videoUrl && (!s.slides || !s.slidesData || s.slidesData.length === 0)).length,
        slideshow: screens.filter(s => s.slides && s.slidesData && s.slidesData.length > 0).length,
        video: screens.filter(s => s.videoUrl).length
      }
      
      if (screenTypes.singleImage > screenTypes.slideshow + screenTypes.video) {
        insights.push({
          title: 'Content Variety',
          insight: `You have ${screenTypes.singleImage} static images but only ${screenTypes.slideshow + screenTypes.video} dynamic screens`,
          action: 'Try creating slideshows or video clips to increase engagement',
          type: 'content'
        })
      }
      
      if (screenTypes.video === 0) {
        insights.push({
          title: 'Video Content',
          insight: 'No video clips detected in your content library',
          action: 'Add video content to capture more attention and increase dwell time',
          type: 'content'
        })
      }
      
      if (screens.length >= 3) {
        insights.push({
          title: 'Content Management',
          insight: `You have ${screens.length} active screens in your library`,
          action: 'Consider organizing screens into categories for better management',
          type: 'management'
        })
      }
    }
    
    // Device-based insights
    if (devices.length > 0) {
      const onlineDevices = devices.filter(d => d.isOnline).length
      const offlineDevices = devices.length - onlineDevices
      
      if (offlineDevices > 0) {
        insights.push({
          title: 'Device Health',
          insight: `${offlineDevices} of ${devices.length} devices are currently offline`,
          action: 'Check device connections and network status',
          type: 'performance'
        })
      }
      
      if (devices.length === 1) {
        insights.push({
          title: 'Network Expansion',
          insight: 'You have only one device connected to your network',
          action: 'Consider adding more devices to expand your digital signage reach',
          type: 'growth'
        })
      }
      
      if (devices.length >= 5) {
        insights.push({
          title: 'Network Scale',
          insight: `Your network spans ${devices.length} devices across multiple locations`,
          action: 'Consider implementing centralized content management',
          type: 'management'
        })
      }
    }
    
    // Schedule-based insights (if schedule data is available)
    const totalSchedules = devices.reduce((total, device) => {
      return total + (device.scheduleCount || 0)
    }, 0)
    
    if (totalSchedules === 0 && devices.length > 0) {
      insights.push({
        title: 'Scheduling Opportunity',
        insight: 'No scheduled content detected across your devices',
        action: 'Set up automated schedules to display content at optimal times',
        type: 'optimization'
      })
    }
    
    if (totalSchedules > 0) {
      insights.push({
        title: 'Schedule Management',
        insight: `You have ${totalSchedules} active schedules across your network`,
        action: 'Review and optimize your content scheduling strategy',
        type: 'management'
      })
    }
    
    // Subscription-based insights
    if (!hasAdminPrivileges(getEffectiveUser())) {
      if (devices.length >= deviceLimit * 0.8) {
        insights.push({
          title: 'Capacity Planning',
          insight: `You're using ${Math.round((devices.length / deviceLimit) * 100)}% of your device limit`,
          action: 'Consider upgrading your plan to add more devices',
          type: 'growth'
        })
      }
    }
    
    // Default insights if not enough data
    if (insights.length === 0) {
      insights.push({
        title: 'Getting Started',
        insight: 'Welcome to your digital signage dashboard',
        action: 'Create your first screen and connect a device to get started',
        type: 'onboarding'
      })
    }
    
    // Randomize and limit to 3 insights
    return insights.sort(() => Math.random() - 0.5).slice(0, 3)
  }

  const aiInsights = generateAIInsights()

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 text-white">
          <h1 className="text-2xl font-bold mb-2">Loading Dashboard...</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              Welcome back, {getEffectiveUser()?.displayNameOrEmail || getEffectiveUser()?.displayName || currentUser?.email}! 👋
            </h1>
            <p className="text-primary-100">
              Here's what's happening with your digital signage network today
            </p>
          </div>
          <div className="hidden md:flex items-center space-x-2 bg-white/10 rounded-lg px-4 py-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-medium">AI-Powered Platform</span>
          </div>
          <div className="hidden md:flex items-center space-x-2">
            <AdminBadge currentUserData={getEffectiveUser()} size="sm" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 
                    stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stat.change}
                  </span>
                  {stat.changeType === 'positive' && (
                    <ArrowUpRight className="w-4 h-4 text-green-600 ml-1" />
                  )}
                  {stat.changeType === 'negative' && (
                    <ArrowDownRight className="w-4 h-4 text-red-600 ml-1" />
                  )}
                </div>

                {/* Show screen type breakdown for Active Screens */}
                {stat.title === 'Active Screens' && screens.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                                         {screenTypeBreakdown.singleImage > 0 && (
                       <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                         <Image className="w-3 h-3 mr-1" />
                         {screenTypeBreakdown.singleImage} Single Image
                       </span>
                     )}
                     {screenTypeBreakdown.slideshow > 0 && (
                       <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                         <SquareStack className="w-3 h-3 mr-1" />
                         {screenTypeBreakdown.slideshow} Slideshow
                       </span>
                     )}
                     {screenTypeBreakdown.video > 0 && (
                       <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                         <Video className="w-3 h-3 mr-1" />
                         {screenTypeBreakdown.video} Video Clip
                       </span>
                     )}
                  </div>
                )}
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* AI Insights */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">AI Insights</h2>
                <div className="flex items-center space-x-2">
                  <Brain className="w-5 h-5 text-primary-500" />
                  <span className="text-sm text-primary-600 font-medium">Live Analysis</span>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {aiInsights.map((insight, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border-l-4 border-primary-500">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 mb-1">{insight.title}</h3>
                      <p className="text-sm text-gray-600 mb-2">{insight.insight}</p>
                      <p className="text-sm text-primary-600 font-medium">{insight.action}</p>
                    </div>
                    <div className="ml-4">
                      {insight.type === 'performance' && <TrendingUp className="w-5 h-5 text-green-500" />}
                      {insight.type === 'behavior' && <Users className="w-5 h-5 text-blue-500" />}
                      {insight.type === 'design' && <Target className="w-5 h-5 text-purple-500" />}
                      {insight.type === 'content' && <Image className="w-5 h-5 text-blue-500" />}
                      {insight.type === 'management' && <Monitor className="w-5 h-5 text-purple-500" />}
                      {insight.type === 'growth' && <TrendingUp className="w-5 h-5 text-green-500" />}
                      {insight.type === 'optimization' && <Target className="w-5 h-5 text-orange-500" />}
                      {insight.type === 'onboarding' && <Sparkles className="w-5 h-5 text-primary-500" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      {activity.type === 'screen_created' && <Monitor className="w-4 h-4 text-primary-600" />}
                      {activity.type === 'ai_optimization' && <Brain className="w-4 h-4 text-primary-600" />}
                      {activity.type === 'device_connected' && <Smartphone className="w-4 h-4 text-primary-600" />}
                      {activity.type === 'content_updated' && <Activity className="w-4 h-4 text-primary-600" />}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-xs text-gray-500">{activity.time} • {activity.user}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>



      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <button 
            onClick={() => navigate('/screens', { state: { openCreateModal: 'image' } })}
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <Monitor className="w-6 h-6 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Create Screen</span>
          </button>
          <button 
            onClick={() => navigate('/schedule', { state: { openAddSchedule: true } })}
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <Calendar className="w-6 h-6 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Schedule Content</span>
          </button>
          <button 
            onClick={() => navigate('/devices', { state: { openAddDevice: true } })}
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <Smartphone className="w-6 h-6 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-700">Add Device</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default Dashboard 